import * as React from 'react';
import * as yup from 'yup';
import {faQuestionCircle} from '@fortawesome/pro-light-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {FormProvider, useForm} from 'react-hook-form';
import {useCallback, useEffect, useState} from 'react';
import {useDebounce} from 'usehooks-ts';
import {yupResolver} from '@hookform/resolvers/yup';

import Button from '../../ui/Buttons/Button';
import {CallOut} from '../../ui/CallOut/CallOut';
import {CookiesService} from '../../../client/cookies/cookies.service';
import {InputGroup} from '../../ui/forms/InputGroup/InputGroup';
import {ItemsService} from '../../../client/items/items.service';
import {Item} from '../item.class';
import {Tooltip} from '../../ui/Tooltip/Tooltip';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {validateShipToZipUSandCA} from '../../tools/yup-validators.functions';

const isStaticStatus = (status: string) => {
    const DynamicStatuses = [`in stock`, `drop ship item`, `not currently available. call your service advisor for availability.`];
    return DynamicStatuses.indexOf(status.toLowerCase()) === -1;
};

const DATE_HIGHLIGHT_REGEX =
    /((TOMORROW|MON|TUE|WED|THU|FRI|SAT|SUN)[A-Z.]*)*[ ,-]*(\d|\d{2})*(st|nd|rd|th)*[ ,-]*(JAN|FEB|MAR|MAY|APR|JUL|JUN|AUG|OCT|SEP|NOV|DEC)[ ,]*(\d|\d{2}|\d{4})*(st|nd|rd|th)*([ ,])*'*(\d{2}|\d{4})*\b(\.)*/gi;

export const highlightDates = (message: string): string => {
    return message.replace(DATE_HIGHLIGHT_REGEX, (matchString) => {
        return `<b>${matchString}</b>`;
    });
};

export interface NewZipForm {
    changeZipInput: string;
}

interface StockStatusProps {
    item: Item;
    quickView?: boolean;
    showServiceStatus?: boolean;
    unitsOrd: number;
    user: User;
}

export const StockStatus = ({user, item, quickView, showServiceStatus = true, unitsOrd}: StockStatusProps) => {
    const [errorMessage, setErrorMessage] = useState(``);
    const [estimatedDaysToShip, setEstimatedDaysToShip] = useState<number>(undefined);
    const [expectedArrival, setExpectedArrival] = useState(``);
    const [fetchedUnitsOrdered, setFetchedUnitsOrdered] = useState<number>(undefined);
    const [inStock, setInStock] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [outOfStock, setOutOfStock] = useState(false);
    const [previousZip, setPreviousZip] = useState(``);
    const [serverStatus, setServerStatus] = useState(item.invStatus);
    const [showZipWidget, setShowZipWidget] = useState(false);
    const cookiesService: CookiesService = useService(`cookiesService`);
    const debouncedUnitsOrd = useDebounce(unitsOrd, 300);
    const isMro = !!item.GSDP_ID_s;
    const itemsService: ItemsService = useService(`itemsService`);
    const serverStatusColor = item.statColor;
    const useFormReturn = useForm({mode: `onBlur`, resolver: yupResolver(yup.object({changeZipInput: validateShipToZipUSandCA(true)}))});

    const [currentZip, setCurrentZip] = useState(
        User.getCurrentZip(cookiesService?.getCookie(`zipOverride`), cookiesService?.getCookie(`geo`), user),
    );

    const _fetchStatus = useCallback(
        (zipToFetch: string) => {
            if (serverStatus && isStaticStatus(serverStatus)) {
                return;
            }
            if (!unitsOrd) {
                return;
            }
            setServerStatus(undefined);
            setIsLoading(true);
            itemsService
                .getStockStatus(item.id, unitsOrd || 1, zipToFetch, isMro)
                .then((mroStatus) => {
                    setEstimatedDaysToShip(undefined);
                    setFetchedUnitsOrdered(unitsOrd);
                    if (mroStatus.inStock) {
                        setInStock(true);
                        setOutOfStock(false);
                        setIsLoading(false);
                        setErrorMessage(undefined);
                    } else {
                        setInStock(false);
                        setOutOfStock(true);
                        setIsLoading(false);
                        setErrorMessage(undefined);
                    }
                    if (mroStatus.message) {
                        setExpectedArrival(highlightDates(mroStatus.message));
                    } else {
                        setExpectedArrival(undefined);
                    }
                })
                .catch((lookupError) => {
                    // Revert zip
                    cookiesService.createCookie(`zipOverride`, previousZip);

                    // Hide all progress and success UI
                    setInStock(false);
                    setOutOfStock(false);
                    setIsLoading(false);

                    // Show stock status fallback
                    // For non-Grainger, show error else show estimated days to ship
                    if (!isMro) {
                        setErrorMessage(lookupError.message);
                    } else {
                        setEstimatedDaysToShip(item.dropshipDays_i);
                    }
                });
        },
        [serverStatus, unitsOrd, itemsService, item, isMro, previousZip, cookiesService],
    );

    useEffect(() => {
        if (serverStatus === `Drop Ship Item`) {
            setServerStatus(``);
            _fetchStatus(currentZip);
        }
        if (serverStatus === `In Stock`) {
            _fetchStatus(currentZip);
        }
    }, [item, serverStatus, _fetchStatus, currentZip]);

    useEffect(() => {
        if (fetchedUnitsOrdered && debouncedUnitsOrd && debouncedUnitsOrd > 0 && debouncedUnitsOrd !== fetchedUnitsOrdered) {
            _fetchStatus(currentZip);
        }
    }, [_fetchStatus, currentZip, debouncedUnitsOrd, fetchedUnitsOrdered]);

    const zipSubmit = useCallback(
        (newZipForm: NewZipForm) => {
            setShowZipWidget(false);
            setPreviousZip(currentZip);
            setCurrentZip(newZipForm.changeZipInput);
            cookiesService.createCookie(`zipOverride`, newZipForm.changeZipInput);
            if (serverStatus && isStaticStatus(serverStatus)) {
                return;
            }
            setEstimatedDaysToShip(undefined);
            setExpectedArrival(``);
            setErrorMessage(undefined);
            setInStock(false);
            setOutOfStock(false);
            _fetchStatus(newZipForm.changeZipInput);
        },
        [_fetchStatus, cookiesService, currentZip, serverStatus],
    );

    /**
     * Template
     */
    return (
        <div>
            {isLoading && (
                <div className="-tw-mt-4 tw-mb-2 tw-h-[30px] tw-w-[30px]">
                    <div className="spinner"></div>
                </div>
            )}
            {!isLoading && inStock && (
                <h4
                    className="tw-text-green-200 tw-font-bold"
                    data-e2e="inStockText"
                >
                    In Stock
                </h4>
            )}
            {outOfStock && <h4 data-e2e="outOfStockText">Out of Stock</h4>}
            {serverStatus && showServiceStatus && (
                <h4
                    className={`${
                        serverStatusColor === 'red' ? ' tw-text-red-500' : serverStatusColor === 'green' ? ' tw-text-green-200' : ''
                    }`}
                >
                    {serverStatus}
                </h4>
            )}
            {errorMessage && <h4 className="tw-text-red-500 tw-font-bold">{errorMessage}</h4>}
            <div className={`tw-mb-3 tw-flex tw-justify-between`}>
                <div>
                    Shipping to <span suppressHydrationWarning>{currentZip}</span>
                </div>
                {!showZipWidget && (
                    <Button
                        className="!tw-px-4"
                        data-e2e="changeLocLink"
                        onClick={() => {
                            setShowZipWidget(!showZipWidget);
                        }}
                        size="sm"
                        variant="link"
                    >
                        Change Location
                    </Button>
                )}
            </div>
            {estimatedDaysToShip && <div className="tw-mb-3">Estimated business days to ship {estimatedDaysToShip}</div>}
            {showZipWidget && (
                <FormProvider {...useFormReturn}>
                    <form
                        data-e2e="zipCodeWidget"
                        onSubmit={(event) => {
                            setErrorMessage(``);
                            useFormReturn
                                .handleSubmit(zipSubmit)(event)
                                .catch((handleSubmitErr) => {
                                    setErrorMessage(handleSubmitErr.message);
                                });
                        }}
                    >
                        <InputGroup
                            button={
                                <Button
                                    aria-label="Apply Zip code"
                                    className="!tw-text-base"
                                    data-e2e="applyBtn"
                                    type="submit"
                                    size="lg"
                                    variant="secondary"
                                >
                                    Apply
                                </Button>
                            }
                            className="maskPII"
                            data-e2e="inputZipCode"
                            error={useFormReturn?.formState?.errors?.changeZipInput?.message as string}
                            label="Zip code"
                            name="changeZipInput"
                            type="text"
                            {...useFormReturn.register(`changeZipInput`)}
                        />
                    </form>
                </FormProvider>
            )}
            <div className={`${expectedArrival && !quickView ? 'tw-h-[52px]' : ''}`}>
                {isLoading && (
                    <div className="tw-h-[30px] tw-w-[30px]">
                        <div className="spinner"></div>
                    </div>
                )}
                {!isLoading && expectedArrival && (
                    <CallOut>
                        <span dangerouslySetInnerHTML={{__html: expectedArrival}}></span>
                        <div className="tw-inline-block tw-ml-2">
                            <Tooltip
                                id="expectedArrival"
                                title="Expected arrival dates are estimates and may change depending on potential shipping delays."
                            >
                                <FontAwesomeIcon
                                    className="tw-text-gray-400"
                                    icon={faQuestionCircle}
                                />
                            </Tooltip>
                        </div>
                    </CallOut>
                )}
                {!isLoading && !expectedArrival && outOfStock && <CallOut>Delivery Date Unavailable</CallOut>}
            </div>
        </div>
    );
};
